import {Button} from '@primer/react'
import {use, useCallback, useImperativeHandle, useState} from 'react'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {CommentsContext, type ViewMode} from './CommentsContext'
import {useCopilotQuotaGate} from '../toolbar/use-copilot-quota-gate'
import {isReviewCommentsViewMode} from './CommentsViewControls'

export interface IssueCommentComposerHandle {
  /** Inserts quoted text into the draft, appending it to any existing draft when `append` is set. */
  insertQuote: (quotedText: string, options?: {append?: boolean}) => void
}

export interface IssueCommentComposerProps {
  /** Renders nothing while true, but keeps the in-progress draft. */
  collapsed?: boolean
  ref?: React.Ref<IssueCommentComposerHandle>
}

/**
 * Composer for a new pull request timeline (conversation) comment. Owns the draft state so it
 * can be dropped in anywhere conversation comments are rendered.
 */
export function IssueCommentComposer({collapsed = false, ref}: IssueCommentComposerProps) {
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending} = useCreatePullRequestTimelineCommentMutation()

  // The draft is retained while collapsed, but the quota gate should stay idle until it is
  // visible again — otherwise it would fetch quota data for a composer nobody can see.
  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(collapsed ? '' : commentText)

  const handleSubmitComment = useCallback(() => {
    if (quotaSubmitDisabled || isPending || commentText.trim().length === 0) return

    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          setCommentText('')
          requestAnimationFrame(() => {
            ssrSafeDocument
              ?.getElementById(`issue-comment-${data.issueComment.id}`)
              ?.scrollIntoView({behavior: 'smooth'})
          })
        },
      },
    )
  }, [commentText, createTimelineComment, isPending, quotaSubmitDisabled])

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(quotedText, {append = false} = {}) {
        setCommentText(prev => (append && prev.trim().length > 0 ? `${prev}\n\n${quotedText}` : quotedText))
      },
    }),
    [],
  )

  if (collapsed) return null

  return (
    <>
      {quotaBanner}
      <ConversationCommentBox
        label="Add a comment"
        value={commentText}
        onChange={setCommentText}
        onPrimaryAction={handleSubmitComment}
        disabled={isPending}
        maxHeightLines={15}
      >
        <Button
          variant="primary"
          onClick={handleSubmitComment}
          inactive={isPending || commentText.trim().length === 0 || quotaSubmitDisabled}
        >
          Comment
        </Button>
      </ConversationCommentBox>
    </>
  )
}

/**
 * Whether the conversation comment composer should be offered: only in conversation view,
 * and only when the current user is allowed to comment.
 */
export function isIssueCommentComposerEnabled(viewMode: ViewMode, currentUserCanComment: boolean): boolean {
  return !isReviewCommentsViewMode(viewMode) && currentUserCanComment
}

/**
 * Context-backed variant of {@link isIssueCommentComposerEnabled}, for components rendered
 * inside `CommentsContext`.
 */
export function useIsIssueCommentComposerEnabled(): boolean {
  const viewMode = use(CommentsContext).viewMode
  const currentUserCanComment = useLayoutCanComment()

  return isIssueCommentComposerEnabled(viewMode, currentUserCanComment)
}
